<template>
  <section>
    <div class="card">
      <header class="header-card">
        <p> {{ $t('addEditBonReception.rapprochements.informations')}} </p>
      </header>
      <div class="card-content">
        <div class="columns">
          <div class="column is-3 ellipsis">
            <b-field
              :label="$t('addEditBonReception.rapprochements.labelFournisseur')"
              custom-class="is-italic"
              class="hex-text-default">
              <span class="has-text-weight-bold">
                {{ fournisseur }}
              </span>
            </b-field>
          </div>
        </div>
      </div>
    </div>
    <div class="card mgt-10px">
      <header class="header-card">
        <p> {{ $t('addEditBonReception.rapprochements.rapprochements')}} </p>
      </header>
      <div class="card-content">
        <div v-if="!detail" class="level">
          <div class="level-left">
            <div class="columns">
              <form @submit.prevent="recherche()" class="column">
                <b-input
                  v-model="critereRecherche.auto"
                  :placeholder="$t('general.button.search')"
                  type="search"
                  icon="magnify">
                </b-input>
              </form>
            </div>
          </div>
        </div>
        <v-client-table
          v-if="lignesCommandeFilter.length > 0"
          :data="lignesCommandeFilter"
          :columns="columns"
          :options="options"
          class="tableWithChildRow">
          <template #produit="{ row }">
            <span v-if="row.produitId">
              {{ row.produit.libelle }}
            </span>
          </template>
          <template #quantite ="{ row }">
            <div class="level">
              <div class="level-left">
                <b-icon v-bind="getIconData(row)"></b-icon>
              </div>
              <div class="level-right">
                <span class="mgl-30px has-text-weight-bold">
                  {{ row.quantite }}
                </span>
              </div>
            </div>
          </template>
          <template #quantiteResteARapprocher="{ row }">
            <div class="level">
              <div class="level-left">
                <b-icon v-bind="getIconData(row)"></b-icon>
              </div>
              <div class="level-right">
                <span class="mgl-30px has-text-weight-bold">
                  {{ getQteResteARapprocher(row) }}
                </span>
              </div>
            </div>
          </template>
          <template #child_row="{ row, index }">
            <rapprochements-ligne-child-row
              :data="row"
              :detail="detail"
              @update:rapprochements="updateRapprochements($event, index)"/>
          </template>
        </v-client-table>
        <div class="card" v-else>
          <div class="card-content">
            <div class="columns">
              <div class="column is-12">
                <p class="has-text-centered title is-size-5">
                  {{ message }}
                </p>
              </div>
              </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import * as tableConfig from '@/configuration/clientTableConf';
import RapprochementsLigneChildRow from '@/components/achat/commande/addEdit/rapprochementsLigneChildRow';

export default {
  name: 'RapprochementsTab',
  components: {
    RapprochementsLigneChildRow,
  },
  props: {
    commande: {
      type: Object,
      required: true,
    },
    detail: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      critereRecherche: { auto: '' },
    };
  },
  computed: {
    lignesCommandeFilter() {
      return this.commande.lignesCommande.filter((ligne) => ligne.produitId);
    },
    fournisseur() {
      const find = this.commande.intervenants.find((i) => !!i.tiersId);
      return find ? find.nomCommercial : null;
    },
    message() {
      let msg = 'addEditBonReception.rapprochements.message.needFournisseur';
      if (this.fournisseur) {
        msg = this.detail
          ? 'rapprochementAchat.error.noCommandeAchatRapprocher'
          : 'rapprochementAchat.error.noCommandeAchatARapprocher';
      }
      return this.$t(msg, { fournisseur: this.fournisseur });
    },
    columnsData() {
      return [
        { name: 'libelle' },
        { name: 'produit' },
        { name: 'quantite' },
        { name: 'quantiteResteARapprocher' },
      ];
    },
    columns() {
      return this.columnsData.map((column) => column.name);
    },
    options() {
      const texts = cloneDeep(tableConfig.TEXTS);
      texts.count = '';
      const headings = this.columnsData.reduce((acc, val) => {
        const key = `${this.detail ? 'detailBonReception' : 'addEditBonReception'}.rapprochements.table.${val.name}`;
        acc[val.name] = this.$te(key) ? this.$t(key) : '';
        return acc;
      }, {});
      return {
        texts,
        headings,
        filterable: false,
        sortIcon: tableConfig.SORT_ICON,
        perPage: tableConfig.RESULTS_PER_PAGE,
        perPageValues: tableConfig.NO_PER_PAGE_VALUE,
        sortable: [],
        columnsClasses: {
          libelle: 'min250px',
          produit: 'max250px',
          quantite: 'colonneSelected',
          quantiteResteARapprocher: 'colonneWith180',
        },
      };
    },
  },
  methods: {
    getQteResteARapprocher(row) {
      const sommeRapprochements = row.rapprochements.reduce((acc, val) => acc + val.quantite, 0);
      return row.quantite - sommeRapprochements;
    },
    getIconData(row) {
      if (this.getQteResteARapprocher(row) < 0) return { icon: 'alert', type: 'is-warning' };
      if (this.getQteResteARapprocher(row) === 0) return { icon: 'check-circle', type: 'is-success' };
      return { icon: '', type: '' };
    },
    updateRapprochements(rapprochements, index) {
      if (!index) return;
      const ligneCommande = this.lignesCommandeFilter[index - 1];
      this.$set(ligneCommande, 'rapprochements', rapprochements);
      const resteARapprocher = this.getQteResteARapprocher(ligneCommande);
      this.$set(ligneCommande, 'quantiteResteARapprocher', resteARapprocher);
    },
    recherche() {
      this.$emit('searchLigneCommandeARapprocher', this.critereRecherche.auto);
    },
  },
};
</script>
